<template>
  <label
    ref="subscribeLabel"
    tabindex="0"
    :aria-label="translations.subscribe.title"
    class="pdp-purchase-option"
    :class="{ 'is-checked': selectedProductType === PRODUCT_TYPE.SUBSCRIBE }"
  >
    <input
      name="purchase"
      type="radio"
      :value="PRODUCT_TYPE.SUBSCRIBE"
      :checked="selectedProductType === PRODUCT_TYPE.SUBSCRIBE"
      @change="setProductType(PRODUCT_TYPE.SUBSCRIBE)"
    />
    <div class="pdp-purchase-option-inner">
      <div class="pdp-purchase-option-main">
        <h5>{{ translations.subscribe.title }}</h5>
        <span>{{ translations.subscribe.subTitle }}</span>
      </div>
      <div class="pdp-purchase-option-price">
        <strong>
          {{ currentPriceFormatted }}
          <span v-if="currentCostPerItem">
            {{ currentCostPerItem }}
          </span>
        </strong>
      </div>
    </div>
    <PurchaseOptionDropdown
      v-if="
        selectedProductType === PRODUCT_TYPE.SUBSCRIBE &&
          context !== CONTEXT.MACHINE
      "
      :title="translations.subscribe.chooseFrequency.title"
      :subtitle="translations.subscribe.chooseFrequency.subTitle"
      :options="chooseSubscriptionFrequencyOptions"
      :value="selectedSellingPlan.id"
      @change="selectSellingPlan($event)"
    />
    <PurchaseOptionDropdown
      v-if="
        selectedProductType === PRODUCT_TYPE.SUBSCRIBE &&
          variants &&
          variants.length > 1 &&
          variantSelectionEnabled
      "
      :title="translations.subscribe.chooseVariant.title"
      :subtitle="translations.subscribe.chooseVariant.subTitle"
      :options="chooseSubscriptionVariantOptions"
      :value="selectedVariant.id"
      @change="selectVariant($event)"
    />
    <PurchaseOptionDropdown
      v-if="
        selectedProductType === PRODUCT_TYPE.SUBSCRIBE &&
          shouldShowFreeGiftDropdown
      "
      :title="freeGift.title"
      :subtitle="freeGift.subTitle"
      :options="freeGift.options"
      :value="selectedFreeGift.id"
      @change="onFreeGiftChange"
    />
    <PurchaseOptionDropdown
      v-if="
        selectedProductType === PRODUCT_TYPE.SUBSCRIBE &&
          upSell &&
          upSell.inventoryQuantity > 0
      "
      :title="upSell.dropdown.title"
      :subtitle="upSell.dropdown.subTitle"
      :options="upSell.options"
      :value="selectedUpsellProductQuantity"
      @change="setUpSellQuantity($event)"
    />
  </label>
</template>

<script>
import * as ShopifyCurrency from '@shopify/theme-currency';
import $dom from 'cash-dom';

import { mapState, mapMutations } from 'vuex';
import { purchaseForm, mutations } from './store';
import { PRODUCT_TYPE, CONTEXT } from './const';

import PurchaseOptionDropdown from './PurchaseOptionDropdown.vue';

import { costPerItemMixin } from './cost_per_item';

export default {
  name: 'ShopifySubscriptionPurchaseOption',

  components: {
    PurchaseOptionDropdown
  },

  mixins: [costPerItemMixin],

  computed: {
    ...mapState(purchaseForm, [
      'context',
      'freeGift',
      'moneyFormat',
      'requiresSellingPlan',
      'selectedFreeGift',
      'selectedProductType',
      'selectedSellingPlan',
      'selectedUpsellProductQuantity',
      'selectedVariant',
      'sellingPlans',
      'shouldShowFreeGiftDropdown',
      'subscribe',
      'translations',
      'upSell',
      'variants',
      'quantitySelector',
      'variantSelectionEnabled'
    ]),

    PRODUCT_TYPE: () => PRODUCT_TYPE,

    CONTEXT: () => CONTEXT,

    currentPrice() {
      const { price: basePrice } = this.selectedVariant;

      if (this.selectedSellingPlan) {
        const {
          price_adjustments: priceAdjustments
        } = this.selectedSellingPlan;

        const discountPercentage = priceAdjustments[0].value;
        const discount = (discountPercentage / 100) * basePrice;

        return basePrice - discount;
      }

      return basePrice;
    },

    currentPriceFormatted() {
      if (this.context === CONTEXT.MACHINE) {
        return ShopifyCurrency.formatMoney(
          this.subscribe.price,
          this.moneyFormat
        );
      }

      return ShopifyCurrency.formatMoney(this.currentPrice, this.moneyFormat);
    },

    chooseSubscriptionVariantOptions() {
      return this.variants.map(item => {
        return {
          label: item.title,
          value: item.id
        };
      });
    },

    chooseSubscriptionFrequencyOptions() {
      return this.sellingPlans.map(({ id, name }) => {
        return {
          label: name,
          value: id
        };
      });
    }
  },

  mounted() {
    $dom(document.body).on('keydown', e => {
      const { keyCode } = e;

      if (
        (keyCode === 32 || keyCode === 13) &&
        document.activeElement === this.$refs.subscribeLabel
      ) {
        e.preventDefault();

        this.setProductType(PRODUCT_TYPE.SUBSCRIBE);
      }
    });
  },

  methods: {
    ...mapMutations({
      setProductType: `${purchaseForm}/${mutations.SET_PRODUCT_TYPE}`,
      setUpSellQuantity: `${purchaseForm}/${mutations.SET_UPSELL_QUANTITY}`,
      setFreeGift: `${purchaseForm}/${mutations.SET_FREE_GIFT}`,
      selectVariant: `${purchaseForm}/${mutations.SELECT_VARIANT}`,
      selectSellingPlan: `${purchaseForm}/${mutations.SELECT_SELLING_PLAN}`
    }),

    onFreeGiftChange(freeGiftId) {
      const newFreeGift = this.freeGift.collection.find(
        freeGift => freeGift.id === freeGiftId
      );

      this.setFreeGift(newFreeGift);
    }
  }
};
</script>
